import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Chip, IconButton, Tooltip, Collapse, Grid, TextField,
  Select, MenuItem, FormControl, InputLabel, Button, CircularProgress, Alert,
  Avatar, Stack, Divider, InputAdornment,
} from '@mui/material';
import AddIcon           from '@mui/icons-material/Add';
import ExpandMoreIcon    from '@mui/icons-material/ExpandMore';
import ExpandLessIcon    from '@mui/icons-material/ExpandLess';
import DeleteIcon        from '@mui/icons-material/Delete';
import SearchIcon        from '@mui/icons-material/Search';
import WorkIcon          from '@mui/icons-material/Work';
import SaveIcon          from '@mui/icons-material/Save';
import HistoryIcon       from '@mui/icons-material/History';
import apiService, { authFetch } from '../../services/apiServices';
import useConfirm from '../../hooks/useConfirm';
import { activeWorkers } from '../../utils/workerStatus';

const API_URL = process.env.REACT_APP_API_URL || '';

// Always send JSON; auth header is injected by authFetch. Kept as a constant
// so existing call-sites that spread `authHeader()` continue to work.
const authHeader = () => ({ 'Content-Type': 'application/json' });

function fmt(str) {
  if (!str) return '—';
  try { return new Date(str).toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return str; }
}

function initials(name) {
  return (name || '').split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

// ── Inline assignment row (used inside expanded worker section) ───────────────
function AssignRow({ workerId, projects, onSaved }) {
  const [form, setForm]     = useState({ project_id: '', start_date: '', end_date: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  const save = async () => {
    if (!form.project_id || !form.start_date) return setErr('Projekti dhe data e fillimit janë të detyrueshme');
    setSaving(true); setErr('');
    try {
      const r = await authFetch(`${API_URL}/api/workers/${workerId}/project-assignments`, {
        method: 'POST', headers: authHeader(),
        body: JSON.stringify({ project_id: Number(form.project_id), start_date: form.start_date, end_date: form.end_date || null }),
      });
      const d = await r.json();
      if (!r.ok || !d.success) { setErr(d.message || d.error || 'Gabim'); }
      else { setForm({ project_id: '', start_date: '', end_date: '' }); onSaved(); }
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Box sx={{ mt: 1.5 }}>
      {err && <Alert severity="error" sx={{ mb: 1, py: 0.5, fontSize: '0.78rem' }}>{err}</Alert>}
      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} sm={4} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel sx={{ fontSize: '0.8rem' }}>Projekti</InputLabel>
            <Select value={form.project_id} label="Projekti" onChange={e => { setForm(f => ({ ...f, project_id: e.target.value })); setErr(''); }}
              sx={{ fontSize: '0.82rem' }}>
              <MenuItem value=""><em>— Zgjidhni —</em></MenuItem>
              {projects.map(p => <MenuItem key={p.Id || p.id} value={p.Id || p.id} sx={{ fontSize: '0.82rem' }}>{p.Name || p.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3} md={2.5}>
          <TextField fullWidth size="small" type="date" label="Fillon" InputLabelProps={{ shrink: true }}
            value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))}
            sx={{ '& input': { fontSize: '0.82rem' } }} />
        </Grid>
        <Grid item xs={6} sm={3} md={2.5}>
          <TextField fullWidth size="small" type="date" label="Mbaron (opsionale)" InputLabelProps={{ shrink: true }}
            value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
            inputProps={{ min: form.start_date || undefined }}
            sx={{ '& input': { fontSize: '0.82rem' } }} />
        </Grid>
        <Grid item xs={12} sm={2} md={2}>
          <Button fullWidth variant="contained" size="small" startIcon={saving ? <CircularProgress size={13} color="inherit" /> : <SaveIcon />}
            onClick={save} disabled={saving} sx={{ fontSize: '0.78rem', textTransform: 'none', height: 36 }}>
            Ruaj
          </Button>
        </Grid>
      </Grid>
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
        Nëse ka caktim aktiv pa datë mbarimi, ai mbyllet automatikisht me datën e ditës para fillimit të ri.
      </Typography>
    </Box>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function StaffProjectsPage() {
  const [workers,     setWorkers]     = useState([]);
  const [projects,    setProjects]    = useState([]);
  const [assignments, setAssignments] = useState({}); // { workerId: [] }
  const [expanded,    setExpanded]    = useState({});  // { workerId: bool }
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [filterProj,  setFilterProj]  = useState('');

  // load workers + projects once
  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const [wR, pR] = await Promise.all([
        apiService().get('workers').catch(() => ({ success: false, data: [] })),
        apiService().get('projects').catch(() => ({ success: false, data: [] })),
      ]);
      setWorkers(activeWorkers(wR.data));
      setProjects(pR.data || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadBase(); }, [loadBase]);

  // load assignment history for a specific worker on expand
  const loadAssignments = useCallback(async (workerId) => {
    if (assignments[workerId]) return; // already loaded
    const r = await authFetch(`${API_URL}/api/workers/${workerId}/project-assignments`, { headers: authHeader() });
    const d = await r.json();
    setAssignments(prev => ({ ...prev, [workerId]: d.data || [] }));
  }, [assignments]);

  const refreshWorker = useCallback(async (workerId) => {
    const r = await authFetch(`${API_URL}/api/workers/${workerId}/project-assignments`, { headers: authHeader() });
    const d = await r.json();
    setAssignments(prev => ({ ...prev, [workerId]: d.data || [] }));
  }, []);

  const toggleExpand = (workerId) => {
    const next = !expanded[workerId];
    setExpanded(prev => ({ ...prev, [workerId]: next }));
    if (next) loadAssignments(workerId);
  };

  const [confirm, confirmUI] = useConfirm();


  const handleDelete = async (workerId, assignId) => {
    if (!(await confirm({ title: 'Fshi caktimin', message: 'Fshi këtë caktim?', confirmLabel: 'Fshi' }))) return;
    await authFetch(`${API_URL}/api/workers/${workerId}/project-assignments/${assignId}`, {
      method: 'DELETE', headers: authHeader(),
    });
    refreshWorker(workerId);
  };

  // current project per worker (most recent with no end_date or latest end_date)
  const currentProject = useCallback((workerId) => {
    const list = assignments[workerId];
    if (!list) return null;
    const today = new Date().toISOString().slice(0, 10);
    const active = list.find(a => a.start_date <= today && (!a.end_date || a.end_date >= today));
    return active || null;
  }, [assignments]);

  const projectName = useCallback((id) => {
    const p = projects.find(p => String(p.Id || p.id) === String(id));
    return p ? (p.Name || p.name) : `#${id}`;
  }, [projects]);

  // filtered workers
  const filtered = useMemo(() => {
    let list = workers;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(w => {
        const name = `${w.FirstName || w.first_name || ''} ${w.LastName || w.last_name || ''}`.toLowerCase();
        return name.includes(q);
      });
    }
    return list;
  }, [workers, search]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#EFF6FF', display: 'flex' }}>
          <WorkIcon sx={{ color: '#2563EB', fontSize: 24 }} />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#0F172A', lineHeight: 1.2 }}>
            Staf / Projektet
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Cakto dhe menaxho caktimet e punëtorëve nëpër projekte
          </Typography>
        </Box>
      </Stack>

      {/* ── Filters ─────────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ px: 2, py: 1.5, mb: 2, borderRadius: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} sm={5} md={4}>
            <TextField
              fullWidth size="small" placeholder="Kërko punëtor..."
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} /></InputAdornment> }}
              sx={{ '& input': { fontSize: '0.85rem' } }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.85rem' }}>Filtro sipas projektit</InputLabel>
              <Select value={filterProj} label="Filtro sipas projektit"
                onChange={e => setFilterProj(e.target.value)} sx={{ fontSize: '0.85rem' }}>
                <MenuItem value="">— Të gjithë —</MenuItem>
                {projects.map(p => <MenuItem key={p.Id || p.id} value={String(p.Id || p.id)} sx={{ fontSize: '0.85rem' }}>{p.Name || p.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs="auto">
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pt: 0.5 }}>
              {filtered.length} punëtorë
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#475569', width: 32 }} />
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#475569' }}>Punëtori</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#475569' }}>Projekti aktual</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#475569' }}>Nga data</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#475569', width: 90 }} align="center">Historiku</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary', fontSize: '0.85rem' }}>
                      Asnjë punëtor nuk u gjet
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((w) => {
                  const wid  = w.Id || w.id;
                  const name = `${w.FirstName || w.first_name || ''} ${w.LastName || w.last_name || ''}`.trim();
                  const pos  = w.Position || w.position || '';
                  const isOpen = !!expanded[wid];
                  const hist   = assignments[wid] || [];
                  const today  = new Date().toISOString().slice(0, 10);
                  const cur    = hist.find(a => a.start_date <= today && (!a.end_date || a.end_date >= today));

                  // filter by project if filterProj set
                  if (filterProj && (!cur || String(cur.project_id) !== filterProj)) return null;

                  return (
                    <React.Fragment key={wid}>
                      {/* ── Worker row ────────────────────────────────── */}
                      <TableRow
                        hover
                        onClick={() => toggleExpand(wid)}
                        sx={{ cursor: 'pointer', bgcolor: isOpen ? '#F0F9FF' : 'white',
                          '&:hover': { bgcolor: isOpen ? '#E0F2FE' : '#F8FAFC' } }}
                      >
                        <TableCell sx={{ pr: 0 }}>
                          <IconButton size="small" sx={{ p: 0.3 }}>
                            {isOpen ? <ExpandLessIcon sx={{ fontSize: 17 }} /> : <ExpandMoreIcon sx={{ fontSize: 17 }} />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1.2}>
                            <Avatar sx={{ width: 30, height: 30, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#2563EB' }}>
                              {initials(name)}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, lineHeight: 1.2 }}>{name}</Typography>
                              {pos && <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{pos}</Typography>}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {cur
                            ? <Chip label={cur.project_name || projectName(cur.project_id)} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.78rem', height: 22 }} />
                            : <Typography sx={{ fontSize: '0.78rem', color: 'text.disabled' }}>—</Typography>}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                          {cur ? fmt(cur.start_date) : '—'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={<HistoryIcon sx={{ fontSize: '13px !important' }} />}
                            label={isOpen ? 'Mbyll' : 'Shiko'}
                            size="small"
                            variant={isOpen ? 'filled' : 'outlined'}
                            color={isOpen ? 'primary' : 'default'}
                            sx={{ fontSize: '0.72rem', height: 22, cursor: 'pointer' }}
                          />
                        </TableCell>
                      </TableRow>

                      {/* ── Expanded section ──────────────────────────── */}
                      <TableRow sx={{ bgcolor: '#F8FBFF' }}>
                        <TableCell colSpan={5} sx={{ p: 0, borderBottom: isOpen ? '1px solid #E2E8F0' : 'none' }}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 3, py: 2 }}>

                              {/* History table */}
                              <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                                Historiku i caktimeve
                              </Typography>
                              {hist.length === 0 ? (
                                <Typography sx={{ fontSize: '0.8rem', color: 'text.disabled', mb: 1.5 }}>
                                  Asnjë caktim i regjistruar ende.
                                </Typography>
                              ) : (
                                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, borderRadius: 1.5 }}>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.8 }}>Projekti</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.8 }}>Fillon</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.8 }}>Mbaron</TableCell>
                                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.8 }}>Statusi</TableCell>
                                        <TableCell sx={{ width: 40 }} />
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {hist.map(a => (
                                        <TableRow key={a.id} hover>
                                          <TableCell sx={{ fontSize: '0.82rem', fontWeight: 500 }}>
                                            {a.project_name || projectName(a.project_id)}
                                          </TableCell>
                                          <TableCell sx={{ fontSize: '0.82rem' }}>{fmt(a.start_date)}</TableCell>
                                          <TableCell sx={{ fontSize: '0.82rem' }}>{a.end_date ? fmt(a.end_date) : '—'}</TableCell>
                                          <TableCell>
                                            <Chip
                                              size="small"
                                              label={!a.end_date ? 'Aktiv' : 'Përfunduar'}
                                              color={!a.end_date ? 'success' : 'default'}
                                              sx={{ fontSize: '0.7rem', height: 20 }}
                                            />
                                          </TableCell>
                                          <TableCell align="right">
                                            <Tooltip title="Fshi">
                                              <IconButton size="small" color="error"
                                                onClick={e => { e.stopPropagation(); handleDelete(wid, a.id); }}>
                                                <DeleteIcon sx={{ fontSize: 15 }} />
                                              </IconButton>
                                            </Tooltip>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              )}

                              {/* Add assignment form */}
                              <Divider sx={{ mb: 1.5 }}>
                                <Chip icon={<AddIcon />} label="Caktim i ri" size="small" sx={{ fontSize: '0.75rem' }} />
                              </Divider>
                              <AssignRow
                                workerId={wid}
                                projects={projects}
                                onSaved={() => refreshWorker(wid)}
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      {confirmUI}
    </Box>
  );
}
